import type { KosztSesjiDto } from "@shared/kosztyTypy.js";
import { formatujLiczbe, kosztDoPokazania, opisKursu } from "../../lib/koszty.js";
import { odmien } from "../../lib/polski.js";
import { Badge } from "../ui/Badge.js";
import { Card } from "../ui/Card.js";
import { KpiWartosc } from "../ui/KpiTile.js";
import { StanBledu, StanPusty } from "../ui/stany.js";
import { TabelaModeli } from "./TabelaKosztow.js";

/**
 * Karta "Koszt analizy" na stronie rozmowy: ile kosztowaly wywolania modeli tej analizy, z podzialem na modele.
 *
 * To SZACUNEK z cennika (cennik/cennik.json) i zuzycia zapisanego w dzienniku, nie faktura. Karta liczy WSZYSTKIE
 * przebiegi tej analizy, takze ponowione ("Oceń ponownie"), wiec jej suma moze byc wieksza niz to, co widac w
 * raporcie (ten pokazuje tylko najnowszy przebieg kazdego parametru). Mowi o tym podpis.
 *
 * Cztery stany w tej samej karcie (S-5): blad, pusto, niepelny i komplet. Kafelki to KpiWartosc w osobnych divach
 * (karta w karcie jest zakazana, T-2), bez trendu: KpiTile wymusza zielony wzrost, a wzrost kosztu to nie sukces.
 *
 * Plakietki "koszt niepelny" (naglowek) i "niepelny" (tabela) sa w kolorze informacji, nie ostrzezenia: karta stoi na
 * stronie analizy zaraz pod karta oceny, a tam bursztyn to zloto oceny, czyli "najlepsze" (decyzja autora metodyki
 * z 29.10.2026). Sens niesie slowo i przedrostek "od" (ST-3). Na stronie "Koszty" te same plakietki zostaja ostrzezeniem.
 */

export function KartaKosztu({ koszt }: { koszt: KosztSesjiDto }): React.JSX.Element {
  if (koszt.stan === "blad") {
    return (
      <Card tytul="Koszt analizy">
        <StanBledu tytul="Nie udało się policzyć kosztu" opis={koszt.powod} />
      </Card>
    );
  }

  const { podsumowanie, kurs, powodBrakuKursu } = koszt;
  const { suma } = podsumowanie;

  if (podsumowanie.wiersze.length === 0) {
    return (
      <Card tytul="Koszt analizy">
        <StanPusty
          tytul="Brak zapisanych wywołań modeli"
          opis="Ta analiza nie ma wpisów w dzienniku wywołań: powstała przed jego zapisem albo żaden model nic jeszcze nie policzył."
        />
      </Card>
    );
  }

  const powodySumy = [...new Set(podsumowanie.wiersze.flatMap((w) => (w.kompletny ? [] : w.powody)))];
  const calosc = kosztDoPokazania(suma.usd, suma.kompletna, powodySumy, kurs);

  return (
    <Card tytul="Koszt analizy" akcje={suma.kompletna ? undefined : <Badge wariant="info">koszt niepełny</Badge>}>
      <div className="metryki">
        <div>
          <KpiWartosc
            etykieta="Koszt łącznie"
            wartosc={calosc.usd}
            druga={calosc.pln ?? undefined}
            powodBraku={calosc.powod ?? undefined}
          />
        </div>
        <div>
          <KpiWartosc
            etykieta="Wywołania modeli"
            wartosc={formatujLiczbe(suma.wywolania)}
            druga={`${formatujLiczbe(suma.proby)} ${odmien(suma.proby, "próba", "próby", "prób")}`}
          />
        </div>
      </div>

      <TabelaModeli
        wiersze={podsumowanie.wiersze}
        suma={suma}
        kurs={kurs}
        opis="Koszt analizy wg modelu"
        wariantNiepelnego="info"
      />

      <p className="koszt-podpis">
        Szacunek wg cennika z wersji {podsumowanie.wersjaCennika}, nie faktura. Obejmuje wszystkie przebiegi tej analizy,
        także ponowione („Oceń ponownie”), więc może być większy niż to, co pokazuje raport.
        {kurs ? ` ${opisKursu(kurs)}` : powodBrakuKursu ? ` Kwoty w złotych: brak (${powodBrakuKursu}).` : ""}
      </p>
      {podsumowanie.ostrzezenia.length > 0 && (
        <ul className="koszt-ostrzezenia">
          {podsumowanie.ostrzezenia.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}
